import { reactive, watch } from 'vue'
import { getHeats, getSchedule } from '@api'
import { searchGetter } from '@store/search.js'
import { systemApi } from '@/axios-instance.js'
import { WATCH_LEVELS, normalizeWatcherLevel } from '@/utils/watchLevel.js'

const store = reactive({
  watchers: [],
  watcherTabs: [],
  groups: {},
  statusGroups: {},
  timeGroups: {},
  schedule: {},
  activeDayData: {},
  compitionId: null,
  scheduleDelayMinutes: 0,
  scheduleIsLocal: false,
  intevalUpdateId: null,
  autoUpdateIntervalMinutes: 3,
  autoUpdateEnabled: true,
  scheduleRefreshing: false,
  scheduleLoaded: false,
  scrutineerError: '',
  loadingWatchersCompetitionId: null,
  serverCompetitionIds: [],
  scheduleLoadId: 0
})

let watchersHasBeenGet = false

const SCHEDULE_DELAY_STORAGE_KEY = 'scheduleDelayMinutesByCompetitionV1'
const MAX_SCHEDULE_DELAY_MINUTES = 12 * 60
const MIN_SCHEDULE_DELAY_MINUTES = -12 * 60
const WATCHER_TAB_ID_MAX_LENGTH = 64
const AUTO_UPDATE_SETTINGS_STORAGE_KEY = 'scheduleAutoUpdateSettingsV1'
const DEFAULT_AUTO_UPDATE_INTERVAL_MINUTES = 3
const MIN_AUTO_UPDATE_INTERVAL_MINUTES = 1
const MAX_AUTO_UPDATE_INTERVAL_MINUTES = 60

let autoUpdateSettingsLoaded = false

const normalizeCompetitionId = value => String(value ?? '').trim()
const normalizeText = value => String(value ?? '').trim()
const normalizeTextLower = value => normalizeText(value).toLowerCase()

const normalizeAutoUpdateIntervalMinutes = rawValue => {
  const value = Number(rawValue)
  if (!Number.isFinite(value)) return DEFAULT_AUTO_UPDATE_INTERVAL_MINUTES
  return Math.min(
    MAX_AUTO_UPDATE_INTERVAL_MINUTES,
    Math.max(MIN_AUTO_UPDATE_INTERVAL_MINUTES, Math.round(value))
  )
}

const loadAutoUpdateSettings = () => {
  if (autoUpdateSettingsLoaded) return
  autoUpdateSettingsLoaded = true
  if (typeof localStorage === 'undefined') return

  try {
    const raw = localStorage.getItem(AUTO_UPDATE_SETTINGS_STORAGE_KEY)
    if (!raw) return
    const settings = JSON.parse(raw)
    store.autoUpdateIntervalMinutes = normalizeAutoUpdateIntervalMinutes(
      settings?.intervalMinutes
    )
    store.autoUpdateEnabled = settings?.enabled !== false
  } catch (e) {
    console.error('read auto update settings failed', e)
  }
}

const persistAutoUpdateSettings = () => {
  if (typeof localStorage === 'undefined') return

  try {
    localStorage.setItem(
      AUTO_UPDATE_SETTINGS_STORAGE_KEY,
      JSON.stringify({
        intervalMinutes: store.autoUpdateIntervalMinutes,
        enabled: store.autoUpdateEnabled
      })
    )
  } catch (e) {
    console.error('save auto update settings failed', e)
  }
}

const normalizeWatcherTabId = rawValue =>
  normalizeTextLower(rawValue)
    .slice(0, WATCHER_TAB_ID_MAX_LENGTH)
    .replace(/\s+/g, '-')

const normalizeWatcherTagIds = tags => {
  const list = Array.isArray(tags) ? tags : []
  const uniq = []
  const seen = new Set()
  list.forEach(tag => {
    const id = normalizeWatcherTabId(tag)
    if (!id || seen.has(id)) return
    seen.add(id)
    uniq.push(id)
  })
  return uniq
}

const normalizeWatcherTabs = tabs => {
  const list = Array.isArray(tabs) ? tabs : []
  const uniq = new Map()

  list.forEach(rawTab => {
    const tab =
      rawTab && typeof rawTab === 'object'
        ? rawTab
        : { id: rawTab, name: rawTab }
    const id = normalizeWatcherTabId(tab?.id || tab?.name)
    if (!id || uniq.has(id)) return
    const name = normalizeText(tab?.name || tab?.id || id).slice(
      0,
      WATCHER_TAB_ID_MAX_LENGTH
    )
    uniq.set(id, {
      id,
      name: name || id,
      showCategory: tab?.showCategory !== false
    })
  })

  return [...uniq.values()]
}

const normalizeWatchersStatePayload = payload => {
  const raw =
    payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null
  const watchersInput = Array.isArray(payload) ? payload : raw?.watchers
  const watchers = normalizeWatchers(watchersInput)
  const tabs = normalizeWatcherTabs(raw?.tabs)

  if (!tabs.length) {
    return {
      watchers: watchers.map(item => ({ ...item, tags: [] })),
      tabs: []
    }
  }

  const tabIdSet = new Set(tabs.map(tab => tab.id))
  return {
    watchers: watchers.map(item => ({
      ...item,
      tags: normalizeWatcherTagIds(item?.tags).filter(tagId => tabIdSet.has(tagId))
    })),
    tabs
  }
}

const normalizeScheduleDelayMinutes = raw => {
  const clamp = value =>
    Math.max(MIN_SCHEDULE_DELAY_MINUTES, Math.min(MAX_SCHEDULE_DELAY_MINUTES, Math.trunc(value)))

  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) return 0

    const timeMatch = text.match(/^(-?)(\d{1,2}):(\d{1,2})$/)
    if (timeMatch) {
      const sign = timeMatch[1] === '-' ? -1 : 1
      const hours = Number(timeMatch[2])
      const minutes = Number(timeMatch[3])
      if (Number.isFinite(hours) && Number.isFinite(minutes)) {
        const totalMinutes = sign * (hours * 60 + minutes)
        return clamp(totalMinutes)
      }
    }
  }

  const value = Number(raw)
  if (!Number.isFinite(value)) return 0
  return clamp(value)
}

const readScheduleDelayMap = () => {
  try {
    const raw = localStorage.getItem(SCHEDULE_DELAY_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    console.error('read schedule delay failed', e)
    return {}
  }
}

const writeScheduleDelayMap = delayMap => {
  try {
    localStorage.setItem(SCHEDULE_DELAY_STORAGE_KEY, JSON.stringify(delayMap || {}))
  } catch (e) {
    console.error('save schedule delay failed', e)
  }
}

const getPersistedScheduleDelayMinutes = competitionId => {
  const delayMap = readScheduleDelayMap()
  const normalizedCompetitionId = normalizeCompetitionId(competitionId)

  if (
    normalizedCompetitionId &&
    Object.prototype.hasOwnProperty.call(delayMap, normalizedCompetitionId)
  ) {
    return normalizeScheduleDelayMinutes(delayMap[normalizedCompetitionId])
  }

  return normalizeScheduleDelayMinutes(delayMap.default)
}

const persistScheduleDelayMinutes = (competitionId, delayMinutes) => {
  const delayMap = readScheduleDelayMap()
  const normalizedCompetitionId = normalizeCompetitionId(competitionId)
  const normalizedDelay = normalizeScheduleDelayMinutes(delayMinutes)

  delayMap.default = normalizedDelay
  if (normalizedCompetitionId) {
    delayMap[normalizedCompetitionId] = normalizedDelay
  }
  writeScheduleDelayMap(delayMap)
}

const normalizeWatchLevelValue = rawValue => {
  const raw = Number(rawValue)
  if (!Number.isFinite(raw)) return 0
  return Math.min(5, Math.max(0, Math.trunc(raw)))
}

const normalizeClientClassValue = rawValue => normalizeText(rawValue).slice(0, 32)
const normalizeClientLocationValue = rawValue => normalizeText(rawValue).slice(0, 128)
const normalizeClientContactValue = rawValue => normalizeText(rawValue).slice(0, 128)
const normalizeBirthDateValue = rawValue => {
  const value = normalizeText(rawValue)
  if (!value) return ''
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
}

const unwrapAthlete = athleteLike =>
  athleteLike && typeof athleteLike === 'object' && 'athlete' in athleteLike
    ? athleteLike.athlete
    : athleteLike || {}

const getAthleteBirthDate = athleteLike =>
  normalizeBirthDateValue(unwrapAthlete(athleteLike)?.birthDate ?? athleteLike?.birthDate)

const compactAthlete = athleteLike => {
  const athlete = unwrapAthlete(athleteLike)
  const idRaw = athlete?.link?.athleteId ?? athlete?.athleteId ?? athlete?.id
  const firstName = String(athlete?.fullName?.firstName || '').trim()
  const lastName = String(athlete?.fullName?.lastName || '').trim()
  const patronymic = String(athlete?.fullName?.patronymic || '').trim()
  const athleteClass = normalizeClientClassValue(athleteLike?.class ?? athlete?.class)
  const birthDate = getAthleteBirthDate(athleteLike)

  if (
    (idRaw == null || String(idRaw).trim() === '') &&
    !firstName &&
    !lastName &&
    !patronymic
  ) {
    return null
  }

  const result = {
    fullName: {
      firstName,
      lastName
    }
  }
  if (patronymic) {
    result.fullName.patronymic = patronymic
  }
  if (idRaw != null && String(idRaw).trim() !== '') {
    result.link = { athleteId: String(idRaw).trim() }
    result.athleteId = String(idRaw).trim()
  }
  if (athleteClass) {
    result.class = athleteClass
  }
  if (birthDate) {
    result.birthDate = birthDate
  }
  return result
}

const compactWatcher = watcher => {
  const item = watcher && typeof watcher === 'object' ? watcher : {}
  const athletes = Array.isArray(item.athletes)
    ? item.athletes.map(compactAthlete).filter(Boolean).slice(0, 2)
    : []
  if (!athletes.length) return null

  const groupIdRaw = item?.group?.id
  const groupName = String(item?.group?.name || '').trim()
  const club = String(item?.club || '').trim()
  const city = String(item?.city || '').trim()
  const numberRaw = item?.number
  const number =
    numberRaw == null || String(numberRaw).trim() === '' ? null : String(numberRaw).trim()
  const athleteClasses = [...new Set(athletes.map(athlete => normalizeClientClassValue(athlete?.class)).filter(Boolean))]
  const derivedClientClass = athleteClasses.join(' / ')

  return {
    number,
    athletes,
    group: {
      id: groupIdRaw == null ? '' : String(groupIdRaw).trim(),
      name: groupName
    },
    watchLevel: normalizeWatchLevelValue(item.watchLevel ?? (item.isHot ? 2 : 0)),
    club: normalizeClientLocationValue(item?.club || club),
    city: normalizeClientLocationValue(item?.city || city),
    clientClass: normalizeClientClassValue(item?.clientClass || item?.class || derivedClientClass),
    birthDate: normalizeBirthDateValue(item?.birthDate),
    phone: normalizeClientContactValue(item?.phone),
    telegram: normalizeClientContactValue(item?.telegram),
    instagram: normalizeClientContactValue(item?.instagram),
    tags: normalizeWatcherTagIds(item?.tags)
  }
}

const normalizeWatcher = watcher => {
  const item = compactWatcher(watcher)
  if (!item) return null
  item.watchLevel = normalizeWatcherLevel(item.watchLevel)
  return item
}

const normalizeWatchers = list =>
  Array.isArray(list) ? list.map(normalizeWatcher).filter(Boolean) : []

const pad2 = n => String(n).padStart(2, '0')

const getLocalDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const parseDayDateKey = value => {
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return null
    return `${match[1]}-${match[2]}-${match[3]}`
  }

  if (value && typeof value === 'object') {
    const y = Number(value.y)
    const m = Number(value.m)
    const d = Number(value.d)
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null
    }
    return `${y}-${pad2(m)}-${pad2(d)}`
  }

  return null
}

const dayTimestamp = key => {
  if (!key) return Number.NaN
  return Date.parse(`${key}T00:00:00`)
}

const pickActiveDay = days => {
  if (!Array.isArray(days) || !days.length) return null

  const todayKey = getLocalDateKey()
  const todayTs = dayTimestamp(todayKey)
  const normalized = days.map(day => {
    const key = parseDayDateKey(day?.date)
    return { day, key, ts: dayTimestamp(key) }
  })

  const todayDay = normalized.find(i => i.key === todayKey)?.day
  if (todayDay) return todayDay

  const flagDay = normalized.find(i => i.day?.isCurrent)?.day
  if (flagDay) return flagDay

  const futureOrToday = normalized
    .filter(i => Number.isFinite(i.ts) && i.ts >= todayTs)
    .sort((a, b) => a.ts - b.ts)
  if (futureOrToday.length) return futureOrToday[0].day

  const latestPast = normalized
    .filter(i => Number.isFinite(i.ts))
    .sort((a, b) => b.ts - a.ts)
  if (latestPast.length) return latestPast[0].day

  return days[0]
}

const getRoundTypeRank = raw => {
  const text = String(raw || '').trim().toLowerCase()
  if (!text) return 0

  if (text.includes('финал')) return 1000
  if (text.includes('полуфинал') || /1\s*[\\/]\s*2/.test(text)) return 900
  if (text.includes('четверть') || /1\s*[\\/]\s*4/.test(text)) return 800

  const fractionMatch = text.match(/1\s*[\\/]\s*(\d+)/)
  if (fractionMatch) {
    const denominator = Number(fractionMatch[1])
    if (Number.isFinite(denominator) && denominator > 0) {
      const power = Math.log2(denominator)
      if (Number.isInteger(power)) {
        return 900 - (power - 1) * 100
      }
    }
  }

  const oneRoundMatch = text.match(/^one\s*(\d+)/)
  if (oneRoundMatch) {
    const denominator = Number(oneRoundMatch[1])
    if (Number.isFinite(denominator) && denominator > 0) {
      const power = Math.log2(denominator)
      if (Number.isInteger(power)) {
        return 900 - (power - 1) * 100
      }
    }
  }

  return 100
}

const stageRank = stage => {
  const text = String(stage || '').trim().toLowerCase()
  if (text === 'current') return 3
  if (text === 'future') return 2
  if (text === 'past') return 1
  return 0
}

const normalizeActiveItemRound = (item, groupsById) => {
  const dance = item?.dance || {}
  const rawGroupId = dance?.groupId || dance?.roundId || item?.id
  const group = rawGroupId != null ? groupsById[String(rawGroupId)] : null

  const roundId =
    dance?.roundId || group?.currentRound?.roundId || dance?.groupId || item?.id || ''
  const groupId = dance?.groupId || group?.id || item?.id || roundId
  const roundType = dance?.roundType || group?.currentRound?.type || ''
  const groupName = dance?.group || group?.name || ''

  return {
    ...item,
    id: item?.id ?? String(groupId || roundId),
    dance: {
      ...dance,
      roundId: String(roundId || ''),
      groupId: String(groupId || ''),
      roundType,
      group: groupName
    }
  }
}

const normalizeDanceName = value => String(value ?? '').trim()

const mergeDanceNames = (left, right) => {
  const result = []
  const seen = new Set()

  const push = value => {
    const normalized = normalizeDanceName(value)
    if (!normalized) return
    const key = normalized.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(normalized)
  }

  ;(Array.isArray(left) ? left : []).forEach(push)
  ;(Array.isArray(right) ? right : []).forEach(push)
  return result
}

const collectDanceNamesFromCompetitors = competitors => {
  const list = Array.isArray(competitors) ? competitors : []
  const names = []

  list.forEach(competitor => {
    const dances = Array.isArray(competitor?.dances) ? competitor.dances : []
    dances.forEach(dance => {
      const name = normalizeDanceName(dance?.name)
      if (name) names.push(name)
    })
  })

  return mergeDanceNames([], names)
}

const parseTimeToSeconds = value => {
  const text = String(value || '').trim()
  const match = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (!match) return Number.NaN

  const hours = Number(match[1])
  const minutes = Number(match[2])
  const seconds = Number(match[3] || 0)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return Number.NaN
  }
  return hours * 3600 + minutes * 60 + seconds
}

const resolveItemStageByTime = (item, dayDate, now = new Date(), delayMinutes = 0) => {
  const dayKey = parseDayDateKey(dayDate)
  if (!dayKey || dayKey !== getLocalDateKey(now)) {
    return item?.stage
  }

  const startSec = parseTimeToSeconds(item?.startTime)
  const finishSec = parseTimeToSeconds(item?.finishTime)
  if (!Number.isFinite(startSec) || !Number.isFinite(finishSec)) {
    return item?.stage
  }

  const delaySec = normalizeScheduleDelayMinutes(delayMinutes) * 60
  const nowSec =
    now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds() - delaySec
  if (nowSec >= startSec && nowSec <= finishSec) return 'Current'
  if (nowSec < startSec) return 'Future'
  return 'Past'
}

const getItemTimePriority = (item, dayDate, now = new Date(), delayMinutes = 0) => {
  const dayKey = parseDayDateKey(dayDate)
  if (!dayKey || dayKey !== getLocalDateKey(now)) return null

  const startSec = parseTimeToSeconds(item?.startTime)
  const finishSec = parseTimeToSeconds(item?.finishTime)
  if (!Number.isFinite(startSec) || !Number.isFinite(finishSec)) return null

  const delaySec = normalizeScheduleDelayMinutes(delayMinutes) * 60
  const nowSec =
    now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds() - delaySec
  if (nowSec >= startSec && nowSec <= finishSec) {
    return { active: true, distance: 0, startSec }
  }
  const distanceToStart = Math.abs(startSec - nowSec)
  const distanceToFinish = Math.abs(finishSec - nowSec)
  return {
    active: false,
    distance: Math.min(distanceToStart, distanceToFinish),
    startSec
  }
}

const compareItemsByTimePriority = (nextItem, currentItem, dayDate, delayMinutes = 0) => {
  const nextPriority = getItemTimePriority(nextItem, dayDate, new Date(), delayMinutes)
  const currentPriority = getItemTimePriority(currentItem, dayDate, new Date(), delayMinutes)

  if (!nextPriority && !currentPriority) return 0
  if (nextPriority && !currentPriority) return 1
  if (!nextPriority && currentPriority) return -1

  if (nextPriority.active !== currentPriority.active) {
    return nextPriority.active ? 1 : -1
  }
  if (nextPriority.distance !== currentPriority.distance) {
    return nextPriority.distance < currentPriority.distance ? 1 : -1
  }
  if (nextPriority.startSec !== currentPriority.startSec) {
    return nextPriority.startSec < currentPriority.startSec ? 1 : -1
  }
  return 0
}

const dedupeActiveItemsByRound = (items, groupsById, dayDate, delayMinutes = 0) => {
  const list = Array.isArray(items) ? items : []
  if (!list.length) return []

  const map = new Map()
  list.forEach((sourceItem, index) => {
    const item = normalizeActiveItemRound(sourceItem, groupsById)
    item.stage = resolveItemStageByTime(item, dayDate, new Date(), delayMinutes)
    const groupKey =
      item?.dance?.groupId ||
      item?.dance?.roundId ||
      (item?.id != null ? String(item.id) : `__idx_${index}`)
    const key = `${groupKey}:${item.dance.roundId}`

    const current = map.get(key)
    if (!current) {
      map.set(key, { item, index })
      return
    }

    const currentStageRank = stageRank(current.item?.stage)
    const nextStageRank = stageRank(item?.stage)
    if (nextStageRank > currentStageRank) {
      map.set(key, { item, index })
      return
    }
    if (nextStageRank < currentStageRank) {
      map.set(key, { item: current.item, index: current.index })
      return
    }

    const timePriority = compareItemsByTimePriority(
      item,
      current.item,
      dayDate,
      delayMinutes
    )
    if (timePriority > 0) {
      map.set(key, { item, index })
      return
    }
    if (timePriority < 0) {
      map.set(key, { item: current.item, index: current.index })
      return
    }

    const currentRoundRank = getRoundTypeRank(current.item?.dance?.roundType)
    const nextRoundRank = getRoundTypeRank(item?.dance?.roundType)
    if (nextRoundRank > currentRoundRank) {
      map.set(key, { item, index })
      return
    }
    if (nextRoundRank < currentRoundRank) {
      map.set(key, { item: current.item, index: current.index })
      return
    }

    if (index > current.index) {
      map.set(key, { item, index })
      return
    }
    map.set(key, { item: current.item, index: current.index })
  })

  return [...map.values()]
    .sort((a, b) => a.index - b.index)
    .map(i => i.item)
}

// Recover inconsistent live days without inventing times or floors.
const recoverPublishedRounds = (items, sourceItems, groupsById, dayDate) => {
  if (parseDayDateKey(dayDate) !== getLocalDateKey()) return items
  const result = [...items]
  const dayGroupIds = new Set(sourceItems.map(item => String(item?.dance?.groupId || '')))
  const missing = Object.values(groupsById).filter(group =>
    dayGroupIds.has(String(group.id)) &&
    ['InProgress', 'Ready'].includes(group.state) &&
    group.currentRound?.roundId &&
    !result.some(item => String(item.dance?.groupId) === String(group.id) &&
      String(item.dance?.roundId) === String(group.currentRound.roundId))
  )
  const staleTimes = result.length > 0 && result.every(item => item.stage === 'Past') &&
    sourceItems.some(item => item.stage === 'Current')
  if (!missing.length && !staleTimes) return items

  result.forEach(item => {
    const group = groupsById[String(item.dance?.groupId)]
    const source = sourceItems.find(source => String(source.id) === String(item.id))
    if (source?.stage === 'Current' && item.stage === 'Past' &&
        source.dance?.resultsState !== 'Completed' && group?.state !== 'Completed') {
      item.stage = 'Current'
    }
  })
  missing.forEach(group => {
    result.push({
      id: `published:${group.id}:${group.currentRound.roundId}`,
      stage: 'Current',
      recoveredSchedule: true,
      startTime: '',
      finishTime: '',
      floors: [],
      dance: {
        groupId: String(group.id),
        roundId: String(group.currentRound.roundId),
        group: group.name,
        roundType: group.currentRound.type
      }
    })
  })
  return result
}

const activeItemKey = item =>
  String(
    item?.dance?.roundId ||
      item?.dance?.groupId ||
      item?.id ||
      ''
  ).trim()

const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  if (!token) return null
  return {
    Authorization: `Bearer ${token}`
  }
}

const isLoggedIn = () => Boolean(getAuthHeaders())

const fetchScheduleDelayMinutesFromServer = async competitionId => {
  const normalizedCompetitionId = normalizeCompetitionId(competitionId)
  if (!normalizedCompetitionId || !isLoggedIn()) return null

  try {
    const headers = getAuthHeaders()
    if (!headers) return null

    const response = await systemApi.get(
      `/api/tournaments/delay/${encodeURIComponent(normalizedCompetitionId)}`,
      { headers }
    )
    if (response?.data?.found === false) return null
    return normalizeScheduleDelayMinutes(response?.data?.data?.scheduleDelayMinutes)
  } catch (e) {
    console.error('load schedule delay from server failed', e)
    return null
  }
}

const persistScheduleDelayMinutesToServer = async (competitionId, delayMinutes) => {
  const normalizedCompetitionId = normalizeCompetitionId(competitionId)
  if (!normalizedCompetitionId || !isLoggedIn()) return null

  const headers = getAuthHeaders()
  if (!headers) return null

  const response = await systemApi.put(
    `/api/tournaments/delay/${encodeURIComponent(normalizedCompetitionId)}`,
    { scheduleDelayMinutes: normalizeScheduleDelayMinutes(delayMinutes) },
    { headers }
  )

  return normalizeScheduleDelayMinutes(response?.data?.data?.scheduleDelayMinutes)
}

const uniqueCompetitionIds = values => {
  const set = new Set()
  const list = Array.isArray(values) ? values : []
  list.forEach(value => {
    const id = normalizeCompetitionId(value)
    if (id) set.add(id)
  })
  return [...set]
}

const buildWatchersStatePayload = () => ({
  watchers: normalizeWatchers(store.watchers),
  tabs: normalizeWatcherTabs(store.watcherTabs)
})

const persistWatchersToServer = async statePayload => {
  if (!isLoggedIn()) return

  const headers = getAuthHeaders()
  if (!headers) return

  const competitionId = normalizeCompetitionId(store.compitionId)
  if (!competitionId) return

  await systemApi.put(
    `/api/users/watchers/${encodeURIComponent(competitionId)}`,
    { watchers: statePayload },
    { headers }
  )

  store.serverCompetitionIds = uniqueCompetitionIds([
    ...(Array.isArray(store.serverCompetitionIds) ? store.serverCompetitionIds : []),
    competitionId
  ])
}

const saveWatchers = async function () {
  const statePayload = buildWatchersStatePayload()
  store.watchers = statePayload.watchers
  store.watcherTabs = statePayload.tabs
  localStorage.setItem('watcher', JSON.stringify(statePayload))
  if (isLoggedIn()) {
    try {
      await persistWatchersToServer(statePayload)
    } catch (e) {
      console.error('save watchers to server failed', e)
    }
  }
  watchersAction.setIntervalUpdate()
}
const getAthlete = athletes => unwrapAthlete(athletes)

const athleteIdentity = athleteLike => {
  const athlete = getAthlete(athleteLike || {})
  const athleteId = athlete?.link?.athleteId ?? athlete?.athleteId ?? athlete?.id
  if (athleteId != null && String(athleteId).trim() !== '') {
    return `id:${String(athleteId).trim()}`
  }

  const firstName = String(athlete?.fullName?.firstName || '').trim().toLowerCase()
  const lastName = String(athlete?.fullName?.lastName || '').trim().toLowerCase()
  const patronymic = String(athlete?.fullName?.patronymic || '').trim().toLowerCase()
  return `name:${lastName}|${firstName}|${patronymic}`
}

const pairIdentity = (athlete1, athlete2) => {
  return [athleteIdentity(athlete1), athleteIdentity(athlete2)].sort().join('__')
}

const samePairByIdentity = (left, right) => {
  if (!left?.athletes?.[0] || !right?.athletes?.[0]) return false
  const leftPair = pairIdentity(left.athletes[0], left.athletes[1])
  const rightPair = pairIdentity(right.athletes[0], right.athletes[1])
  return leftPair === rightPair
}

const findWatcherIndex = pairData => {
  const byReference = store.watchers.findIndex(i => i === pairData)
  if (byReference >= 0) return byReference
  return store.watchers.findIndex(i => samePairByIdentity(i, pairData))
}

export const watchersSetter = {
  applyServerState(payload) {
    const state = normalizeWatchersStatePayload(payload)
    store.watchers = state.watchers
    store.watcherTabs = state.tabs
    watchersHasBeenGet = true
    localStorage.setItem('watcher', JSON.stringify(buildWatchersStatePayload()))
  },
  async addWatchers(athlete) {
    const index = findWatcherIndex(athlete)
    if (index == -1) {
      const nextAthlete = normalizeWatcher(athlete)
      if (!nextAthlete) return
      nextAthlete.group = nextAthlete.group || {}
      nextAthlete.group.id =
        nextAthlete.group.id || store.groups?.[nextAthlete.group.name]?.id || ''
      nextAthlete.watchLevel = normalizeWatcherLevel(nextAthlete.watchLevel ?? 0)
      nextAthlete.tags = normalizeWatcherTagIds(nextAthlete.tags)
      store.watchers = store.watchers.concat(nextAthlete)
    }
    saveWatchers()
  },
  async replaceWatchers(data) {
    if (Array.isArray(data)) {
      store.watchers = normalizeWatchers(data)
    } else {
      const state = normalizeWatchersStatePayload(data)
      store.watchers = state.watchers
      store.watcherTabs = state.tabs
    }
    saveWatchers()
  },
  async removeWatcherByIndex(index) {
    store.watchers.splice(index, 1)
    saveWatchers()
  },
  async removeWatcherByPair(pairData) {
    const index = findWatcherIndex(pairData)
    if (index >= 0) {
      store.watchers.splice(index, 1)
      saveWatchers()
    }
  },
  async toggleWatcher(pairData) {
    const index = findWatcherIndex(pairData)
    if (index >= 0) {
      store.watchers.splice(index, 1)
    } else {
      const nextAthlete = normalizeWatcher(pairData)
      if (!nextAthlete) return
      nextAthlete.group = nextAthlete.group || {}
      nextAthlete.group.id =
        nextAthlete.group.id || store.groups?.[nextAthlete.group.name]?.id || ''
      nextAthlete.watchLevel = normalizeWatcherLevel(nextAthlete.watchLevel ?? 0)
      nextAthlete.tags = normalizeWatcherTagIds(nextAthlete.tags)
      store.watchers = store.watchers.concat(nextAthlete)
    }
    saveWatchers()
  },
  async setWatchLevelByIndex(index, watchLevel) {
    if (!store.watchers[index]) return
    store.watchers[index].watchLevel = normalizeWatcherLevel(watchLevel)
    saveWatchers()
  },
  async setWatchLevelByPair(pairData, watchLevel) {
    const index = findWatcherIndex(pairData)
    if (index < 0 || !store.watchers[index]) return
    store.watchers[index].watchLevel = normalizeWatcherLevel(watchLevel)
    saveWatchers()
  },
  async addWatcherTab(rawName) {
    const baseName = normalizeText(rawName)
    if (!baseName) return ''

    const existingTabs = normalizeWatcherTabs(store.watcherTabs)
    const usedIds = new Set(existingTabs.map(tab => tab.id))
    const baseId = normalizeWatcherTabId(baseName)
    if (!baseId) return ''

    let nextId = baseId
    let suffix = 2
    while (usedIds.has(nextId)) {
      nextId = `${baseId}-${suffix}`
      suffix += 1
    }

    store.watcherTabs = existingTabs.concat({
      id: nextId,
      name: baseName,
      showCategory: true
    })
    saveWatchers()
    return nextId
  },
  async removeWatcherTab(tabId) {
    const normalizedTabId = normalizeWatcherTabId(tabId)
    if (!normalizedTabId) return

    store.watcherTabs = normalizeWatcherTabs(store.watcherTabs).filter(
      tab => tab.id !== normalizedTabId
    )
    store.watchers = store.watchers.map(item => ({
      ...item,
      tags: normalizeWatcherTagIds(item?.tags).filter(tag => tag !== normalizedTabId)
    }))
    saveWatchers()
  },
  async renameWatcherTab(tabId, rawName) {
    const normalizedTabId = normalizeWatcherTabId(tabId)
    const nextName = normalizeText(rawName)
    if (!normalizedTabId || !nextName) return

    store.watcherTabs = normalizeWatcherTabs(store.watcherTabs).map(tab => {
      if (tab.id !== normalizedTabId) return tab
      return {
        ...tab,
        name: nextName.slice(0, WATCHER_TAB_ID_MAX_LENGTH)
      }
    })
    saveWatchers()
  },
  async setWatcherTabShowCategory(tabId, showCategory) {
    const normalizedTabId = normalizeWatcherTabId(tabId)
    if (!normalizedTabId) return

    store.watcherTabs = normalizeWatcherTabs(store.watcherTabs).map(tab => {
      if (tab.id !== normalizedTabId) return tab
      return {
        ...tab,
        showCategory: Boolean(showCategory)
      }
    })
    saveWatchers()
  },
  async toggleWatcherTagByIndex(index, tabId) {
    const normalizedTabId = normalizeWatcherTabId(tabId)
    if (!normalizedTabId || !store.watchers[index]) return

    const tabExists = normalizeWatcherTabs(store.watcherTabs).some(
      tab => tab.id === normalizedTabId
    )
    if (!tabExists) return

    const currentTags = normalizeWatcherTagIds(store.watchers[index]?.tags)
    const hasTag = currentTags.includes(normalizedTabId)
    const nextTags = hasTag
      ? currentTags.filter(tag => tag !== normalizedTabId)
      : currentTags.concat(normalizedTabId)

    store.watchers[index] = {
      ...store.watchers[index],
      tags: nextTags
    }
    saveWatchers()
  },
  async toggleWatcherTagByPair(pairData, tabId) {
    const index = findWatcherIndex(pairData)
    if (index < 0) return
    watchersSetter.toggleWatcherTagByIndex(index, tabId)
  },
  async initWatcher(year) {
    if (store.watchers.length == 0) {
      const state = normalizeWatchersStatePayload(
        JSON.parse(localStorage.getItem('watcher') ?? '[]')
      )
      store.watchers = state.watchers
      store.watcherTabs = state.tabs
    }
  },
  setStatusGroups(groups) {
    store.statusGroups = groups
  },
  setGroups(groups) {
    store.groups = groups
  },
  setTimeGroups(timeGroups) {
    store.timeGroups = timeGroups
  },
  setScheduleDelayMinutes(delayMinutes) {
    const normalizedDelay = normalizeScheduleDelayMinutes(delayMinutes)
    store.scheduleDelayMinutes = normalizedDelay
    persistScheduleDelayMinutes(store.compitionId, normalizedDelay)

    if (store.compitionId) {
      persistScheduleDelayMinutesToServer(store.compitionId, normalizedDelay)
        .catch(e => {
          console.error('save schedule delay to server failed', e)
        })
        .finally(() => {
          watchersAction.setSchedule(store.compitionId, store.scheduleIsLocal, {
            scheduleDelayMinutes: normalizedDelay
          })
        })
    }
  },
  setAutoUpdateIntervalMinutes(intervalMinutes) {
    loadAutoUpdateSettings()
    store.autoUpdateIntervalMinutes = normalizeAutoUpdateIntervalMinutes(
      intervalMinutes
    )
    persistAutoUpdateSettings()
    watchersAction.setIntervalUpdate()
  },
  setAutoUpdateEnabled(enabled) {
    loadAutoUpdateSettings()
    store.autoUpdateEnabled = Boolean(enabled)
    persistAutoUpdateSettings()
    watchersAction.setIntervalUpdate()
  }
}

export const watchersGetter = {
  watchers: () => {
    if (!watchersHasBeenGet) {
      watchersHasBeenGet = true
      watchersSetter.initWatcher()
    }
    return store.watchers
  },
  watchersByGroups() {
    return Object.groupBy(store.watchers, w => w.group.name)
  },
  watchersById() {
    const objectByGroupId = Object.groupBy(
      store.watchers,
      w => athleteIdentity(w?.athletes?.[0] || {})
    )

    return objectByGroupId
  },
  competitonId() {
    return store.compitionId
  },
  groups() {
    return store.groups
  },
  statusGroups() {
    return store.statusGroups
  },
  activeDayData() {
    return store.activeDayData
  },

  schedule() {
    return store.schedule
  },
  timeGroups() {
    return store.timeGroups
  },
  scheduleDelayMinutes() {
    return store.scheduleDelayMinutes
  },
  watchLevels() {
    return WATCH_LEVELS
  },
  watcherTabs() {
    return store.watcherTabs
  },
  autoUpdateIntervalMinutes() {
    loadAutoUpdateSettings()
    return store.autoUpdateIntervalMinutes
  },
  autoUpdateEnabled() {
    loadAutoUpdateSettings()
    return store.autoUpdateEnabled
  },
  scheduleRefreshing() {
    return store.scheduleRefreshing
  },
  canLoadScrutineer() {
    return store.scheduleLoaded && !store.scheduleIsLocal && !store.schedule?.groups?.length
  },
  scrutineerError() {
    return store.scrutineerError
  }
}

export const watchersAction = {
  async syncWatchersWithServer() {
    if (!isLoggedIn()) return

    store.loadingWatchersCompetitionId = normalizeCompetitionId(store.compitionId) || null

    try {
      const headers = getAuthHeaders()
      if (!headers) return

      const response = await systemApi.get('/api/users/watchers', { headers })
      const serverState = normalizeWatchersStatePayload({
        watchers: response?.data?.data,
        tabs: response?.data?.tabs
      })
      const serverWatchers = serverState.watchers
      const serverTabs = serverState.tabs
      const byCompetition = Array.isArray(response?.data?.byCompetition)
        ? response.data.byCompetition
        : []
      const serverCompetitionIds = uniqueCompetitionIds(
        byCompetition.map(item => item?.competitionId)
      )
      store.serverCompetitionIds = uniqueCompetitionIds([
        ...serverCompetitionIds,
        store.compitionId
      ])

      if (serverWatchers.length > 0 || serverTabs.length > 0 || store.watchers.length === 0) {
        store.watchers = serverWatchers
        store.watcherTabs = serverTabs
        localStorage.setItem('watcher', JSON.stringify(buildWatchersStatePayload()))
      } else {
        const statePayload = buildWatchersStatePayload()
        store.watchers = statePayload.watchers
        store.watcherTabs = statePayload.tabs
        await persistWatchersToServer(statePayload)
      }
    } catch (e) {
      console.error('load watchers from server failed', e)
    } finally {
      store.loadingWatchersCompetitionId = null
    }
  },
  setSchedule(compitionId, isLocal= false, options = {}) {
    const scheduleLoadId = store.scheduleLoadId + 1
    store.scheduleLoadId = scheduleLoadId
    store.scheduleRefreshing = true
    store.scheduleLoaded = false
    store.scrutineerError = ''
    store.compitionId = compitionId
    const hasDelayOverride = Object.prototype.hasOwnProperty.call(
      options,
      'scheduleDelayMinutes'
    )
    store.scheduleDelayMinutes = hasDelayOverride
      ? normalizeScheduleDelayMinutes(options.scheduleDelayMinutes)
      : getPersistedScheduleDelayMinutes(compitionId)
    store.scheduleIsLocal = Boolean(isLocal)
    watchersAction.setIntervalUpdate()
    watchersAction.syncWatchersWithServer()
    const scheduleDelayPromise = hasDelayOverride
      ? Promise.resolve(null)
      : fetchScheduleDelayMinutesFromServer(compitionId)

    return Promise.all([getSchedule(store.compitionId,isLocal, options), scheduleDelayPromise]).then(([res, serverDelay]) => {
      if (scheduleLoadId !== store.scheduleLoadId) return

      if (serverDelay != null) {
        store.scheduleDelayMinutes = serverDelay
        persistScheduleDelayMinutes(compitionId, serverDelay)
      }
      store.schedule = res.data || {}
      store.scheduleLoaded = true
      const scheduleGroups = Array.isArray(res?.data?.groups) ? res.data.groups : []
      const groupsById = Object.fromEntries(
        scheduleGroups
          .filter(g => g && g.id != null)
          .map(g => [String(g.id), g])
      )
      if (res.data.groups.length == 0) {
        // alert('плохой турнир')
        store.activeDayData = {}
      } else {
        const activeDay = pickActiveDay(Array.isArray(res?.data?.days) ? res.data.days : [])

        if (activeDay) {
          const rawItems = Array.isArray(activeDay.items) ? activeDay.items : []
          let activeItems = rawItems.filter(i => i && typeof i === 'object')

          // Some parser sources return only group ids in day.items.
          // Convert ids into schedule-like objects so the UI can render the day.
          if (!activeItems.length && rawItems.length) {
            activeItems = rawItems
              .map(rawItem => {
                const rawGroupId =
                  rawItem && typeof rawItem === 'object'
                    ? rawItem?.dance?.groupId || rawItem?.dance?.roundId || rawItem?.groupId || rawItem?.id
                    : rawItem
                if (rawGroupId == null || String(rawGroupId).trim() === '') return null

                const group = groupsById[String(rawGroupId)] || null
                const roundId = group?.currentRound?.roundId || group?.id || rawGroupId
                if (roundId == null || String(roundId).trim() === '') return null

                return {
                  id: String(rawGroupId),
                  stage: 'Current',
                  dance: {
                    roundId: String(roundId),
                    groupId: group?.id || rawGroupId,
                    group: group?.name || '',
                    roundType: group?.currentRound?.type || ''
                  }
                }
              })
              .filter(Boolean)
          }
          activeItems = dedupeActiveItemsByRound(
            activeItems,
            groupsById,
            activeDay?.date,
            store.scheduleDelayMinutes
          )
          activeItems = recoverPublishedRounds(
            activeItems, rawItems, groupsById, activeDay.date
          )
          store.activeDayData = { ...activeDay, items: activeItems }
          watchersAction.addPairsToActiveDay(scheduleLoadId)
        }else{
          if (!searchGetter.showOldValue()) {
            store.activeDayData = { date: '', isCurrent: true, items: [] }
            return
          }
          let goodIds = Object.keys(store.groups || {}).map(id=> store.groups[id]).filter(Boolean)
          if(goodIds.length > 0){

            store.activeDayData =  {
              date: '',
              isCurrent: true,
              items: goodIds.map((group)=>{
                return {
                  id: group.id,
                  stage: 'Current',
                  dance:{
                    roundId: group.id,
                    group: group.name,
                    roundType: group.currentRound
                    // resultsState:
                  }
                }
              })
            }
          } else {
            store.activeDayData = { date: '', isCurrent: true, items: [] }
          }
        }
      }
    }).catch((e) => {
      if (scheduleLoadId !== store.scheduleLoadId) return
      console.error('setSchedule failed', e)
      if (options.forceScrutineer) {
        store.scheduleLoaded = true
        store.scrutineerError = e?.response?.data?.error || 'Не удалось загрузить расписание со Scrutineer'
        return
      }
      store.schedule = {
        competition: {
          title: 'Ошибка загрузки расписания',
          city: ''
        },
        groups: [],
        days: []
      }
      store.activeDayData = { date: '', isCurrent: true, items: [] }
    }).finally(() => {
      if (scheduleLoadId === store.scheduleLoadId) {
        store.scheduleRefreshing = false
      }
    })
  },
  setIntervalUpdate() {
    loadAutoUpdateSettings()
    if (store.intevalUpdateId) {
      clearTimeout(store.intevalUpdateId)
      store.intevalUpdateId = null
    }
    if (!store.compitionId || !store.autoUpdateEnabled) return

    store.intevalUpdateId = setTimeout(() => {
      watchersAction.setSchedule(store.compitionId, store.scheduleIsLocal)
    }, store.autoUpdateIntervalMinutes * 60 * 1000)
  },
  refreshSchedule() {
    if (!store.compitionId || store.scheduleRefreshing) return Promise.resolve()
    return watchersAction.setSchedule(store.compitionId, store.scheduleIsLocal)
  },
  loadScrutineer() {
    if (!isLoggedIn() || !store.compitionId || store.scheduleRefreshing || !watchersGetter.canLoadScrutineer()) return Promise.resolve()
    return watchersAction.setSchedule(store.compitionId, false, { forceScrutineer: true })
  },
  updateWatchersData(index, data) {
    store.watchers[index] = data
    saveWatchers()
  },
  addPairData(data, index, scheduleLoadId = store.scheduleLoadId) {
    const sourceItemKey = activeItemKey(data)
    return getHeats(data.dance.roundId, store.compitionId).then(async res => {
      if (scheduleLoadId !== store.scheduleLoadId) return

      const group = store.schedule?.groups?.find(group =>
        String(group.id) === String(data.dance.groupId)
      )
      const publishedRound = group?.currentRound
      let loadedPublishedRound = false
      if (
        !res?.competitors?.length && publishedRound?.roundId &&
        String(publishedRound.roundId) !== String(data.dance.roundId)
      ) {
        const publishedHeats = await getHeats(publishedRound.roundId, store.compitionId)
        if (publishedHeats?.competitors?.length) {
          res = publishedHeats
          loadedPublishedRound = true
        }
      }
      if (scheduleLoadId !== store.scheduleLoadId) return
      let activeItem = store.activeDayData?.items?.[index]
      if (!activeItem || activeItemKey(activeItem) !== sourceItemKey) return
      if (loadedPublishedRound) {
        const publishedIndex = store.activeDayData.items.findIndex(item =>
          String(item?.dance?.groupId) === String(data.dance.groupId) &&
          String(item?.dance?.roundId) === String(publishedRound.roundId)
        )
        if (publishedIndex >= 0) {
          // Keep each scheduled round and its time; attach heats to the round they belong to.
          activeItem.stage = publishedIndex < index ? 'Future' : 'Past'
          activeItem = store.activeDayData.items[publishedIndex]
          activeItem.stage = 'Current'
        } else {
          activeItem.dance = {
            ...activeItem.dance,
            roundId: String(publishedRound.roundId),
            roundType: publishedRound.type || res.roundType || ''
          }
        }
      }

      const heatCompetitors = Array.isArray(res?.competitors) ? res.competitors : []
      let competitors = {}
      heatCompetitors.forEach((i,index)=>{
        const key = pairIdentity(i.athletes?.[0], i.athletes?.[1])
        competitors[key] = i
      })
      activeItem.competitors = competitors
      activeItem.heatCount = res.heatCount
      activeItem.markCount = res.markCount
      const existingDances = Array.isArray(activeItem?.dance?.dances)
        ? activeItem.dance.dances
        : []
      const responseDances = Array.isArray(res?.dances) ? res.dances : []
      const competitorDances = collectDanceNamesFromCompetitors(heatCompetitors)
      // Use the published heats, including Basic dances, rather than another round's program.
      const resolvedDances = competitorDances.length
        ? competitorDances
        : responseDances.length ? responseDances : existingDances
      if (resolvedDances.length) {
        activeItem.dance = activeItem.dance || {}
        activeItem.dance.dances = resolvedDances
      }
      store.activeDayData = { ...store.activeDayData }
    }).catch(e => {
      if (scheduleLoadId !== store.scheduleLoadId) return
      console.error('load heats failed', data?.dance?.roundId, e)
    })
  },
  addPairsToActiveDay(scheduleLoadId = store.scheduleLoadId) {
    store.activeDayData.items.forEach((i, index) => {
      if (i.stage == 'Current') {
        if (i.dance) {
          watchersAction.addPairData(i, index, scheduleLoadId)
        }
      }
    })
  }
}
